import { createContext, useContext, useState, ReactNode } from 'react';

export type ReportTheme = 'classic-red' | 'monochrome-dark' | 'elegant-grey' | 'corporate-white' | 'gradient-crimson' | 'gradient-steel';

interface ReportThemeContextType {
    theme: ReportTheme;
    setTheme: (theme: ReportTheme) => void;
    getThemeClasses: () => ReportThemeClasses;
}

interface ReportThemeClasses {
    // Background classes
    pageBackground: string;
    sectionBackground: string;
    cardBackground: string;
    
    // Text classes
    headingText: string;
    bodyText: string;
    accentText: string;
    
    // Border and accent classes
    cardBorder: string;
    hoverEffect: string;
    buttonPrimary: string;
    buttonSecondary: string;
    
    // Specific elements
    healthRingPrimary: string;
    healthRingSecondary: string;
    metricCardGradient: string;
    problemCardSeverity: {
        high: string;
        medium: string;
        low: string;
    };
}

const ReportThemeContext = createContext<ReportThemeContextType | undefined>(undefined);

const THEME_CONFIGS: Record<ReportTheme, ReportThemeClasses> = {
    'classic-red': {
        pageBackground: 'bg-gradient-to-br from-brand-50 via-white to-gray-50 font-sans',
        sectionBackground: 'bg-white/95 font-sans',
        cardBackground: 'bg-white font-sans',
        headingText: 'text-brand-600 font-sans',
        bodyText: 'text-gray-800 font-sans',
        accentText: 'text-brand-600 font-sans',
        cardBorder: 'border-brand-200',
        hoverEffect: 'hover:shadow-xl hover:border-brand-300 hover:shadow-brand-100/50 hover:scale-[1.02] transition-all duration-300 ease-out',
        buttonPrimary: 'bg-brand-600 hover:bg-brand-700 text-white font-sans',
        buttonSecondary: 'bg-gray-100 hover:bg-gray-200 text-gray-800 font-sans',
        healthRingPrimary: 'stroke-brand-600',
        healthRingSecondary: 'stroke-gray-300',
        metricCardGradient: 'bg-gradient-to-r from-brand-50 to-white',
        problemCardSeverity: {
            high: 'border-brand-600 bg-brand-50 font-sans',
            medium: 'border-gray-400 bg-gray-50 font-sans',
            low: 'border-gray-300 bg-white font-sans'
        }
    },
    'monochrome-dark': {
        pageBackground: 'bg-gradient-to-br from-gray-900 via-black to-gray-800 font-sans',
        sectionBackground: 'bg-gray-800/90 font-sans',
        cardBackground: 'bg-gray-700/95 font-sans',
        headingText: 'text-white font-sans',
        bodyText: 'text-gray-200 font-sans',
        accentText: 'text-brand-600 font-sans',
        cardBorder: 'border-gray-600',
        hoverEffect: 'hover:shadow-xl hover:border-gray-500 hover:shadow-brand-600/20 hover:scale-[1.02] transition-all duration-300 ease-out',
        buttonPrimary: 'bg-brand-600 hover:bg-brand-700 text-white font-sans',
        buttonSecondary: 'bg-gray-600 hover:bg-gray-500 text-white font-sans',
        healthRingPrimary: 'stroke-brand-600',
        healthRingSecondary: 'stroke-gray-600',
        metricCardGradient: 'bg-gradient-to-r from-gray-800 to-black',
        problemCardSeverity: {
            high: 'border-brand-600 bg-brand-900/30 font-sans',
            medium: 'border-gray-400 bg-gray-700/50 font-sans',
            low: 'border-gray-500 bg-gray-800/30 font-sans'
        }
    },
    'elegant-grey': {
        pageBackground: 'bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 font-sans',
        sectionBackground: 'bg-gray-50/90 font-sans',
        cardBackground: 'bg-white/98 font-sans',
        headingText: 'text-gray-900 font-sans',
        bodyText: 'text-gray-700 font-sans',
        accentText: 'text-brand-600 font-sans',
        cardBorder: 'border-gray-300',
        hoverEffect: 'hover:shadow-xl hover:border-gray-400 hover:shadow-gray-200/60 hover:scale-[1.02] transition-all duration-300 ease-out',
        buttonPrimary: 'bg-gray-800 hover:bg-gray-900 text-white font-sans',
        buttonSecondary: 'bg-gray-200 hover:bg-gray-300 text-gray-800 font-sans',
        healthRingPrimary: 'stroke-gray-600',
        healthRingSecondary: 'stroke-gray-300',
        metricCardGradient: 'bg-gradient-to-r from-gray-100 to-white',
        problemCardSeverity: {
            high: 'border-brand-600 bg-brand-50 font-sans',
            medium: 'border-gray-400 bg-gray-100 font-sans',
            low: 'border-gray-300 bg-white font-sans'
        }
    },
    'corporate-white': {
        pageBackground: 'bg-gradient-to-br from-white via-gray-50 to-white font-sans',
        sectionBackground: 'bg-white/95 font-sans',
        cardBackground: 'bg-white font-sans',
        headingText: 'text-black font-sans',
        bodyText: 'text-gray-800 font-sans',
        accentText: 'text-brand-600 font-sans',
        cardBorder: 'border-gray-200',
        hoverEffect: 'hover:shadow-xl hover:border-brand-200 hover:shadow-gray-200/50 hover:scale-[1.02] transition-all duration-300 ease-out',
        buttonPrimary: 'bg-black hover:bg-gray-900 text-white font-sans',
        buttonSecondary: 'bg-gray-100 hover:bg-gray-200 text-black font-sans',
        healthRingPrimary: 'stroke-black',
        healthRingSecondary: 'stroke-gray-300',
        metricCardGradient: 'bg-gradient-to-r from-white to-gray-50',
        problemCardSeverity: {
            high: 'border-brand-600 bg-brand-50 font-sans',
            medium: 'border-gray-400 bg-gray-50 font-sans',
            low: 'border-gray-300 bg-white font-sans'
        }
    },
    'gradient-crimson': {
        pageBackground: 'bg-gradient-to-br from-brand-100 via-white to-gray-100 font-sans',
        sectionBackground: 'bg-gradient-to-r from-brand-50/80 to-white/90 font-sans',
        cardBackground: 'bg-gradient-to-br from-white to-brand-50/30 font-sans',
        headingText: 'text-brand-600 font-sans',
        bodyText: 'text-gray-800 font-sans',
        accentText: 'text-brand-600 font-sans',
        cardBorder: 'border-brand-200',
        hoverEffect: 'hover:shadow-xl hover:border-brand-300 hover:shadow-brand-200/60 hover:scale-[1.03] transition-all duration-300 ease-out',
        buttonPrimary: 'bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-sans',
        buttonSecondary: 'bg-gradient-to-r from-white to-brand-50 hover:from-gray-50 hover:to-brand-100 text-brand-800 font-sans',
        healthRingPrimary: 'stroke-brand-600',
        healthRingSecondary: 'stroke-brand-200',
        metricCardGradient: 'bg-gradient-to-br from-brand-50 via-white to-gray-50',
        problemCardSeverity: {
            high: 'border-brand-600 bg-gradient-to-r from-brand-50 to-brand-100/50 font-sans',
            medium: 'border-gray-400 bg-gradient-to-r from-gray-50 to-white font-sans',
            low: 'border-gray-300 bg-gradient-to-r from-white to-gray-50 font-sans'
        }
    },
    'gradient-steel': {
        pageBackground: 'bg-gradient-to-br from-gray-200 via-gray-100 to-white font-sans',
        sectionBackground: 'bg-gradient-to-r from-gray-100/80 to-white/90 font-sans',
        cardBackground: 'bg-gradient-to-br from-white to-gray-100/40 font-sans',
        headingText: 'text-gray-900 font-sans',
        bodyText: 'text-gray-700 font-sans',
        accentText: 'text-brand-600 font-sans',
        cardBorder: 'border-gray-300',
        hoverEffect: 'hover:shadow-xl hover:border-gray-400 hover:shadow-gray-300/60 hover:scale-[1.03] transition-all duration-300 ease-out',
        buttonPrimary: 'bg-gradient-to-r from-gray-700 to-black hover:from-gray-800 hover:to-gray-900 text-white font-sans',
        buttonSecondary: 'bg-gradient-to-r from-gray-100 to-white hover:from-gray-200 hover:to-gray-100 text-gray-800 font-sans',
        healthRingPrimary: 'stroke-gray-600',
        healthRingSecondary: 'stroke-gray-300',
        metricCardGradient: 'bg-gradient-to-br from-gray-100 via-white to-gray-50',
        problemCardSeverity: {
            high: 'border-brand-600 bg-gradient-to-r from-brand-50 to-white font-sans',
            medium: 'border-gray-400 bg-gradient-to-r from-gray-100 to-white font-sans',
            low: 'border-gray-300 bg-gradient-to-r from-white to-gray-50 font-sans'
        }
    }
};

interface ReportThemeProviderProps {
    children: ReactNode;
    defaultTheme?: ReportTheme;
}

export function ReportThemeProvider({ children, defaultTheme = 'corporate-white' }: ReportThemeProviderProps) {
    const [theme, setTheme] = useState<ReportTheme>(defaultTheme);

    const getThemeClasses = (): ReportThemeClasses => {
        return THEME_CONFIGS[theme];
    };

    return (
        <ReportThemeContext.Provider value={{ theme, setTheme, getThemeClasses }}>
            {children}
        </ReportThemeContext.Provider>
    );
}

export function useReportTheme() {
    const context = useContext(ReportThemeContext);
    if (context === undefined) {
        throw new Error('useReportTheme must be used within a ReportThemeProvider');
    }
    return context;
}